import { FOCUS_RING, cn, linkClasses, useLanguage, useTranslate, CARD_SURFACE } from '@athena/ui'
import { Link } from '@tanstack/react-router'

import { useFormat } from '../../lib/format.js'

export interface PracticeHistoryGroupProps {
  title: string
  attemptCount: number
  /** Null when the tutor's plan does not include complete progress tracking. */
  averagePercent: number | null
  bestPercent: number | null
  attempts: ReadonlyArray<{
    attemptId: string
    percent: number
    correctCount: number
    questionCount: number
    submittedAt: string
    /** Whether the plan opens this attempt answer by answer. The score is always shown. */
    planIncludesReview: boolean
  }>
}

/**
 * One practice a student has worked at, with every attempt under it.
 *
 * The average and the best sit in the header because they are the two numbers
 * worth quoting about a run of attempts; the individual scores sit under them
 * in the order they happened, newest first, so improvement reads down the
 * page. Each row links to the attempt itself, which is where the answers are.
 */
export function PracticeHistoryGroup({
  title,
  attemptCount,
  averagePercent,
  bestPercent,
  attempts,
}: PracticeHistoryGroupProps) {
  const t = useTranslate()
  const language = useLanguage()
  const format = useFormat()

  return (
    <section className={cn(CARD_SURFACE, 'flex min-w-0 flex-col')}>
      <div className="flex flex-col gap-2 border-b border-border p-4 sm:px-6">
        <h3 className="break-words text-h4 font-semibold text-text">{title}</h3>
        <p className="text-body-sm tabular-nums text-text-secondary">
          {averagePercent === null || bestPercent === null
            ? format.count('attempts', attemptCount)
            : t('student.history.summary', [
                averagePercent,
                bestPercent,
                format.count('attempts', attemptCount),
              ])}
        </p>
      </div>

      <ul className="flex flex-col px-2 sm:px-4">
        {attempts.map((attempt) => {
          const score = (
            <span className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span className="text-body font-semibold tabular-nums text-text">
                {format.percent(attempt.percent)}
              </span>
              <span className="text-body-sm tabular-nums text-text-secondary">
                {t('student.history.correctOf', [attempt.correctCount, attempt.questionCount])}
              </span>
            </span>
          )
          const row =
            'flex min-h-touch-target-min flex-wrap items-center justify-between gap-x-4 gap-y-2 rounded-sm px-2 py-3'
          return (
            <li key={attempt.attemptId} className="border-b border-border last:border-b-0">
              {/* An earlier attempt the plan does not open is still a score in
                  the history — listed, dated, and not offered as a link that
                  would only refuse. */}
              {attempt.planIncludesReview ? (
                <Link
                  to="/$language/attempts/$attemptId"
                  params={{ language, attemptId: attempt.attemptId }}
                  className={cn(row, 'transition-colors hover:bg-surface-secondary', FOCUS_RING)}
                >
                  {score}
                  <span className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <span className="text-body-sm tabular-nums text-text-secondary">
                      {format.date(attempt.submittedAt)}
                    </span>
                    <span className={linkClasses({ size: 'sm' })}>{t('common.review')}</span>
                  </span>
                </Link>
              ) : (
                <div className={row}>
                  {score}
                  <span className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <span className="text-body-sm tabular-nums text-text-secondary">
                      {format.date(attempt.submittedAt)}
                    </span>
                    <span className="text-body-sm text-text-secondary">
                      {t('student.history.scoreOnly')}
                    </span>
                  </span>
                </div>
              )}
            </li>
          )
        })}
      </ul>
    </section>
  )
}
